import { FC, useEffect, useMemo, useState } from "react";
import { Form, Image } from "antd";
import { FaSignature } from "react-icons/fa";
import styled from "styled-components";
import { isJSON } from "@lhd/utility";
import { AppPartType } from "../../AppForm/types";
import {
  fetchWorkerDigitalSignature,
  getConfigAppPartType,
  getWorkerDigitalSignature,
} from "../../utils";
import { APP_PART_TYPES } from "../../AppForm/constants";

interface IProps {
  isDisable?: boolean;
  preview?: boolean;
  item: AppPartType;
  parentItem?: AppPartType;
  style?: React.CSSProperties;
  isTable?: boolean;
  nameItem: string;
  form?: any;
  selectionDataList?: any;
  allAppParts?: AppPartType[];
  className?: string;
  styleColor?: React.CSSProperties;
  linkedNameItem?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
}

const SignatureContainer = styled.div<{ $hasSignature: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  max-height: 80px;
  background: ${({ $hasSignature }) => ($hasSignature ? "#fff" : "#fafafa")};
  border: 1px
    ${({ $hasSignature }) =>
      $hasSignature ? "solid #d9d9d9" : "dashed #d9d9d9"};
  border-radius: 4px;
  padding: 6px 12px;
  width: 100%;

  .ant-image {
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 68px;
  }

  img {
    max-height: 64px;
    max-width: 100%;
    object-fit: contain;
  }

  .signature-placeholder {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #8c8c8c;
    font-size: 13px;
  }
`;

const EmployeeSignature: FC<IProps> = ({
  item,
  nameItem,
  form,
  selectionDataList,
  allAppParts,
  style,
  styleColor = {},
  linkedNameItem,
  defaultValue,
  onChange,
}) => {
  const { isHiddenLabel, labelText } = getConfigAppPartType(item);

  const referencedFieldCode = useMemo(() => {
    const directCode =
      item?.referenced_field_code ||
      (item?.item_config_data as any)?.referenced_field_code;
    if (directCode) return directCode;
    const firstWorkerPart = (allAppParts || []).find(
      (p) => p.app_part_type === APP_PART_TYPES.WORKER_SELECTION,
    );
    return firstWorkerPart?.field_code || firstWorkerPart?.id || null;
  }, [item, allAppParts]);

  const linkedFieldName = useMemo(() => {
    if (linkedNameItem) return linkedNameItem;
    if (!referencedFieldCode) return null;
    const matched = (allAppParts || []).find(
      (p) =>
        (p.field_code != null &&
          String(p.field_code) === String(referencedFieldCode)) ||
        (p.id != null && String(p.id) === String(referencedFieldCode)) ||
        (p.item_config_data?.i != null &&
          String(p.item_config_data.i) === String(referencedFieldCode)),
    );
    return (
      matched?.id || matched?.item_config_data?.i || String(referencedFieldCode)
    );
  }, [allAppParts, referencedFieldCode, linkedNameItem]);

  // Watch linked worker selection value if form is available
  const watchedWorkerValue = Form.useWatch(
    linkedFieldName ? String(linkedFieldName) : "",
    form,
  );

  const [signatureUrl, setSignatureUrl] = useState<string | null>(() => {
    return defaultValue || form?.getFieldValue?.(nameItem) || null;
  });

  useEffect(() => {
    let currentWorkerId: string | number | null = null;
    const val =
      watchedWorkerValue !== undefined
        ? watchedWorkerValue
        : form?.getFieldValue?.(linkedFieldName);

    if (Array.isArray(val)) {
      if (val.length === 1) {
        currentWorkerId =
          typeof val[0] === "object" && val[0] !== null ? val[0]?.id : val[0];
      }
    } else if (typeof val === "string") {
      if (isJSON(val)) {
        try {
          const parsed = JSON.parse(val);
          if (Array.isArray(parsed) && parsed.length === 1) {
            currentWorkerId =
              typeof parsed[0] === "object" && parsed[0] !== null
                ? parsed[0]?.id
                : parsed[0];
          } else if (typeof parsed === "number" || typeof parsed === "string") {
            currentWorkerId = parsed;
          }
        } catch (e) {
          // ignore
        }
      } else if (val.trim()) {
        currentWorkerId = val.trim();
      }
    } else if (typeof val === "number") {
      currentWorkerId = val;
    }

    if (!currentWorkerId) {
      setSignatureUrl(null);
      form?.setFieldValue?.(nameItem, "");
      onChange?.("");
      return;
    }

    // Try finding in selectionDataList.worker_selection
    const worker = (selectionDataList?.worker_selection || []).find(
      (w: any) => String(w.id) === String(currentWorkerId),
    );

    let isMounted = true;
    const directSig = getWorkerDigitalSignature(worker);
    if (directSig) {
      setSignatureUrl(directSig);
      form?.setFieldValue?.(nameItem, directSig);
      onChange?.(directSig);
    } else {
      // Fetch full worker details to get signature if not present in worker summary
      fetchWorkerDigitalSignature(currentWorkerId).then((sig) => {
        if (isMounted) {
          setSignatureUrl(sig);
          form?.setFieldValue?.(nameItem, sig || "");
          onChange?.(sig || "");
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [
    watchedWorkerValue,
    linkedFieldName,
    selectionDataList?.worker_selection,
    form,
    nameItem,
    onChange,
  ]);

  return (
    <div style={style}>
      <Form.Item
        label={
          isHiddenLabel ? (
            <div></div>
          ) : (
            <div className="no-wrap-text" title={labelText}>
              {labelText}
            </div>
          )
        }
        name={nameItem}
      >
        <SignatureContainer $hasSignature={!!signatureUrl} style={styleColor}>
          {signatureUrl ? (
            <Image src={signatureUrl} alt="署名" preview={{ mask: "拡大" }} />
          ) : (
            <div className="signature-placeholder">
              <FaSignature size={16} />
              <span>
                {!referencedFieldCode
                  ? "(社員選択未設定)"
                  : "「社員選択」では、社員を1名のみ選択してください。"}
              </span>
            </div>
          )}
        </SignatureContainer>
      </Form.Item>
    </div>
  );
};

export default EmployeeSignature;
